<template>
  <div>
    <v-row>
      <v-col>
        <v-autocomplete
          v-model="model.event_id"
          label="Evento"
          :items="events.map(e => ({ title: eventName(e) + ' | ' + e.configuration?.name, value: e.id }))"
          item-value="value"
          variant="outlined"
          density="compact"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-form :disabled="disabled" fast-fail>
          <v-card class="mb-3">
            <v-card-title>Usuários</v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="4">
                  <v-text-field
                    v-model="search.email"
                    label="Email"
                    variant="outlined"
                    density="compact"
                    hide-details="auto"
                    :rules="[rules.email()]"
                    @keyup.enter="fetchUsers"
                  />
                </v-col>
                <v-col cols="4">
                  <v-text-field
                    v-model="search.document"
                    label="Documento"
                    variant="outlined"
                    density="compact"
                    hide-details="auto"
                    @keyup.enter="fetchUsers"
                  />
                </v-col>
                <v-col cols="4">
                  <v-btn
                    :loading="loader.loading"
                    :disabled="disabled"
                    block
                    color="primary"
                    @click="fetchUsers"
                  >
                    Buscar
                  </v-btn>
                </v-col>
              </v-row>

              <v-row v-if="users.length > 0">
                <v-col>
                  <v-table>
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Nome</th>
                        <th>Documento</th>
                        <th>Email</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="user in users" :key="user.id">
                        <td>{{ user.id.split('-')[0] }}</td>
                        <td>{{ user.name }}</td>
                        <td>{{ formatDocument(user.document, user.document_type) }}</td>
                        <td>{{ user.email }}</td>
                        <td>
                          <v-checkbox v-model="model.user_id" :value="user.id" hide-details />
                        </td>
                      </tr>
                    </tbody>
                  </v-table>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-form>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="6">
        <v-text-field
          ref="name-input"
          v-model="model.name"
          label="Nome da Cortesia"
          variant="outlined"
          density="compact"
          :disabled="disabled"
          :rules="[(v: string) => v && v.length >= 3 || 'O nome da cortesia precisa ter ao menos 3 caracteres']"
        />
      </v-col>
    </v-row>

    <v-form :disabled="disabled" fast-fail @submit.prevent>
      <v-row>
        <v-col cols="12">
          <v-table>
            <thead>
              <tr>
                <th class="text-left">
                  Setor
                </th>
                <th class="text-left">
                  Seção
                </th>
                <th class="text-left">
                  Capacidade
                </th>
                <th class="text-left">
                  Disponível
                </th>
                <th class="text-left" style="width: 200px">
                  Quantidade
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in items" :key="item.typeId">
                <td class="text-left">
                  {{ item.sector }}
                </td>
                <td class="text-left">
                  {{ item.section }}
                </td>
                <td class="text-left">
                  {{ item.capacity }}
                </td>
                <td class="text-left">
                  {{ item.available }}
                </td>
                <td class="text-left">
                  <v-text-field
                    v-model="item.quantity"
                    type="number"
                    min="0"
                    :max="item.available"
                    hide-details
                    variant="outlined"
                    density="compact"
                    @update:model-value="clampQuantity(item)"
                  />
                </td>
              </tr>
            </tbody>
          </v-table>
        </v-col>
      </v-row>
      <v-row justify="end">
        <v-col cols="2">
          <v-btn
            type="submit"
            color="primary"
            block
            :loading="loader.loading"
            :disabled="disabled"
            @click="submit"
          >
            Criar
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
  </div>
</template>

<script lang="ts" setup>
import { formatDocument } from '@/helpers/formatters/document'
import { rules } from '@/helpers/rules'
import { eventName } from '@/plugins/eventName'
import ComplimentaryTicketService, { StorePayload } from '@/services/ComplimentaryTicketService'
import ProducerService from '@/services/ProducerService'
import UserService from '@/services/UserService'
import { useLoader } from '@/stores/loader'
import { useSnackBar } from '@/stores/snackbar'
import Event from '@/types/Event'
import User from '@/types/User'
import { DateTime, Interval } from 'luxon'
import { computed, onMounted, ref, useTemplateRef, watch } from 'vue'
import type { VTextField } from 'vuetify/lib/components'

interface TableItem {
  typeId: string
  sector: string
  section: string
  capacity: number
  available: number
  quantity: number
}

const loader = useLoader()

const snackbar = useSnackBar()

const model = defineModel<StorePayload>({ required: true })

const emit = defineEmits<{
  submit: [ model: StorePayload, clearFields: () => Promise<void> ]
}>()

const nameInput = useTemplateRef<VTextField>('name-input')

const disabled = computed(() => loader.loading || items.value.length === 0)

// Items mounting

const items = ref<TableItem[]>([])

watch(() => model.value.event_id, mountItems)

async function mountItems() {
  let sectors = await fetchSectors(model.value.event_id)

  sectors = sectors.sort((a, b) => a.order - b.order).filter(s => s.active)

  items.value = []

  for (const sector of sectors) {
    for (const section of sector.sections.filter(s => s.active)) {
      const type = section.ticket_types?.find(t => t.name === 'Cortesia')

      if (!type?.batches || type.batches.length === 0) continue

      const sold = (sector.reserved ?? 0) + (sector.sold ?? 0)

      items.value.push({
        typeId: type.id,
        sector: sector.name,
        section: section.name,
        capacity: sector.capacity,
        available: sector.capacity - sold,
        quantity: 0,
      })
    }
  }

  if (items.value.length === 0) {
    snackbar.show('Não há tipos ou lotes de cortesias configurados para o evento selecionado.', 'info')
  }
}

function clampQuantity(item: TableItem) {
  let quantity = Number(item.quantity.toString().replace(/[^0-9]/g, ''))

  quantity = isNaN(quantity) ? 0 : quantity

  item.quantity = Math.min(item.available, Math.max(0, quantity))
}

// Submit

function submit() {
  model.value.ticket_types = items.value.map(item => ({ id: item.typeId, quantity: item.quantity }))

  const errorMessages = validate()

  if (errorMessages.length > 0) {
    snackbar.show(errorMessages.join(' '), 'error')
    return
  }
  emit('submit', model.value, clearFields)
}

function validate() {
  const messages: string[] = []
  if (model.value.ticket_types.every(t => t.quantity === 0)) {
    messages.push('É necessário fornecer a quantidade de tickets de ao menos um tipo.')
  }
  if (!model.value.user_id) {
    messages.push('Usuário não selecionado.')
  }
  if (!nameInput.value?.isValid) {
    messages.push('É necessário fornecer o nome da cortesia.')
  }
  return messages
}

async function clearFields() {
  users.value = []
  search.value.email = ''
  search.value.document = ''
  model.value.user_id = ''
  model.value.ticket_types = []

  await nameInput.value?.reset()

  return await mountItems()
}

// Users

const users = ref<User[]>([])

const search = ref({ email: '', document: '' })

async function fetchUsers() {
  if (!search.value.document && !search.value.email) return
  loader.increment()
  try {
    const filters = new URLSearchParams()

    for (const [key, value] of Object.entries(search.value)) {
      if (!value) continue
      filters.append(key, value)
    }

    const { data } = await (new UserService()).index(1, filters.toString(), 100)

    users.value = data.data

    if (users.value.length === 1) {
      model.value.user_id = users.value[0].id
    } else if (users.value.length === 0) {
      snackbar.show('Usuário não encontrado em nossa base de cadastros', 'info')
    }
  } finally {
    loader.decrement()
  }
}

// Events

const events = ref<Event[]>([])

async function fetchEvents() {
  loader.increment()
  try {
    const params = new URLSearchParams({
      'pos.active': 'true',
      'pos.online_sales': 'true',
      'end_between': Interval.fromDateTimes(DateTime.now(), DateTime.now().plus({ years: 5 })).toISO(),
    })

    const response = await new ProducerService().events(1, params.toString(), 1000)

    const events = response.data.data

    if (events.length === 0) {
      snackbar.show('Nenhum evento disponível no momento.', 'info')
    }

    return events
  } finally {
    loader.decrement()
  }
}

async function fetchSectors(eventId: string) {
  loader.increment()
  try {
    const response = await (new ComplimentaryTicketService()).sectors(eventId)

    response.data.data.forEach((sector) => {
      sector.sections.forEach((section) => {
        if (!section.ticket_types?.length && section.tickets?.length) {
          section.ticket_types = section.tickets
        }
      })
    })

    return response.data.data
  } finally {
    loader.decrement()
  }
}

onMounted(async () => {
  events.value = await fetchEvents()
  if (events.value.length > 0) {
    model.value.event_id = events.value[0].id
  }
})

</script>
